import type { Playlist, PlaylistAssignment } from "../../types/dashboard";

interface Props {
  assignments: PlaylistAssignment[];
  playlists: Playlist[];
  onChange: (next: PlaylistAssignment[]) => void;
  readOnly?: boolean;
}

const DAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];

export function PlaylistAssignmentEditor({
  assignments,
  playlists,
  onChange,
  readOnly = false,
}: Props) {
  function update(idx: number, patch: Partial<PlaylistAssignment>) {
    onChange(assignments.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  }
  function remove(idx: number) {
    onChange(assignments.filter((_, i) => i !== idx));
  }
  function add() {
    const first = playlists[0];
    if (!first) return;
    onChange([
      ...assignments,
      {
        playlist_id: first.id,
        start_time: null,
        end_time: null,
        days_of_week: "0,1,2,3,4,5,6",
      },
    ]);
  }
  function toggleDay(idx: number, dayIdx: number) {
    const cur = (assignments[idx].days_of_week ?? "0,1,2,3,4,5,6")
      .split(",")
      .map((d) => parseInt(d.trim(), 10))
      .filter((n) => !Number.isNaN(n));
    const set = new Set(cur);
    if (set.has(dayIdx)) set.delete(dayIdx);
    else set.add(dayIdx);
    update(idx, { days_of_week: Array.from(set).sort().join(",") });
  }

  return (
    <>
      {assignments.length === 0 ? (
        <div style={{ color: "var(--text2)", fontSize: 13, padding: "12px 0" }}>
          No playlists assigned. Click the button below to add one.
        </div>
      ) : (
        assignments.map((a, idx) => {
          const hasSchedule = !!(a.start_time && a.end_time);
          const days = (a.days_of_week ?? "0,1,2,3,4,5,6")
            .split(",")
            .map((d) => parseInt(d.trim(), 10))
            .filter((n) => !Number.isNaN(n));
          return (
            <div key={idx} className="sched-row">
              <select
                value={a.playlist_id}
                onChange={(e) => update(idx, { playlist_id: Number(e.target.value) })}
                disabled={readOnly}
              >
                {playlists.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <label className="sched-toggle">
                <input
                  type="checkbox"
                  checked={hasSchedule}
                  disabled={readOnly}
                  onChange={(e) =>
                    update(idx, {
                      start_time: e.target.checked ? "09:00" : null,
                      end_time: e.target.checked ? "17:00" : null,
                    })
                  }
                />
                Schedule
              </label>
              {hasSchedule && (
                <>
                  <input
                    type="time"
                    value={a.start_time ?? "09:00"}
                    onChange={(e) => update(idx, { start_time: e.target.value })}
                    disabled={readOnly}
                  />
                  <span style={{ color: "var(--text2)" }}>to</span>
                  <input
                    type="time"
                    value={a.end_time ?? "17:00"}
                    onChange={(e) => update(idx, { end_time: e.target.value })}
                    disabled={readOnly}
                  />
                  <div className="sched-days">
                    {DAY_LABELS.map((l, di) => (
                      <label key={di}>
                        <input
                          type="checkbox"
                          checked={days.includes(di)}
                          disabled={readOnly}
                          onChange={() => toggleDay(idx, di)}
                        />
                        {l}
                      </label>
                    ))}
                  </div>
                </>
              )}
              {!readOnly && (
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  style={{ marginLeft: "auto" }}
                  onClick={() => remove(idx)}
                  aria-label="Remove assignment"
                >
                  ×
                </button>
              )}
            </div>
          );
        })
      )}
      {!readOnly && (
        <button type="button" className="btn-add-playlist" onClick={add} disabled={!playlists.length}>
          + Add Playlist
        </button>
      )}
    </>
  );
}
